// Themes
import { createTheme } from "@mui/material/styles";

// Constants
import {
  TITLE_FONT_WEIGHT,
  DEFAULT_FONT_FAMILY,
  BORDER_RADIUS,
  BUTTON_BORDER_RADIUS,
  BUTTON_MIN_HEIGHT,
  MODES,
} from "Constants/theme";
import {
  boxShadow,
  boxShadowHover,
  mainThemeColours,
} from "Constants/ColourConstants";

//Utils
import { alpha, darken } from "@mui/material";

// Theme
import {
  getMuiButtonRootStyles,
  getMuiDisabledStyles,
  muiSnackbarDefaultProps,
} from "./common";

export const MBSTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: mainThemeColours.primary[mode] },
      secondary: { main: mainThemeColours.secondary[mode] },
      error: { main: mainThemeColours.error[mode] },
      background: {
        default: mainThemeColours.background[mode],
        paper: mainThemeColours.paper[mode],
        elevated: mainThemeColours.elevated[mode],
      },
    },
    typography: {
      fontFamily: DEFAULT_FONT_FAMILY,
      h1: {
        fontSize: "1.5rem",
        "@media (max-width:600px)": { fontSize: "1.4rem" },
        color: mainThemeColours.primary[mode],
        fontWeight: TITLE_FONT_WEIGHT,
      },
      h2: {
        fontSize: "1.4rem",
        "@media (max-width:600px)": { fontSize: "1.3rem" },
        color: mainThemeColours.primary[mode],
        fontWeight: TITLE_FONT_WEIGHT,
      },
      h3: {
        fontSize: "1.3rem",
        "@media (max-width:600px)": { fontSize: "1.25rem" },
        color: mainThemeColours.primary[mode],
        fontWeight: TITLE_FONT_WEIGHT,
      },
      h4: {
        fontSize: "1.2rem",
        color: mainThemeColours.primary[mode],
        fontWeight: TITLE_FONT_WEIGHT,
      },
      h5: {
        fontSize: "1.15rem",
        color: mainThemeColours.primary[mode],
        fontWeight: TITLE_FONT_WEIGHT,
      },
      h6: {
        fontSize: "1.1rem",
        color: mainThemeColours.primary[mode],
      },
      body1: { color: mainThemeColours.text[mode], lineHeight: 1.5 },
      body2: { color: mainThemeColours.text[mode], lineHeight: 1.5 },
      subtitle1: { color: mainThemeColours.altText[mode], lineHeight: 1.2 },
      subtitle2: { color: mainThemeColours.altText[mode], lineHeight: 1.2 },
      button: { textTransform: "none", lineHeight: 1.2 },
    },
    shape: { borderRadius: BORDER_RADIUS },
    components: {
      MuiButton: {
        styleOverrides: {
          root: getMuiButtonRootStyles,
          contained: ({ theme }) => ({
            minHeight: BUTTON_MIN_HEIGHT,
            backgroundColor: mainThemeColours.primary[mode],
            boxShadow,
            "&:hover": {
              boxShadow: boxShadowHover,
              backgroundColor: darken(
                mainThemeColours.primary[mode],
                mode === MODES.DARK ? 0.16 : 0.08
              ),
            },
            ...getMuiDisabledStyles(theme),
          }),
          outlined: ({ ownerState }) => {
            const isError = ownerState.color === "error";
            const buttonColor = isError
              ? mainThemeColours.error[mode]
              : mainThemeColours.secondary[mode];

            return {
              minHeight: BUTTON_MIN_HEIGHT,
              boxShadow,
              borderWidth: 1.5,
              borderStyle: "solid",
              color: buttonColor,
              borderColor: buttonColor,
              "&:hover": {
                boxShadow: boxShadowHover,
                borderColor: buttonColor,
                backgroundColor: alpha(buttonColor, 0.06),
              },
            };
          },
          text: { color: mainThemeColours.text[mode] },
        },
      },
      MuiSnackbar: {
        defaultProps: muiSnackbarDefaultProps,
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            borderRadius: BUTTON_BORDER_RADIUS,
            padding: "0.25rem 0.75rem",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { padding: "1rem", "--Paper-overlay": "none" },
        },
      },
      MuiPickersPopper: { styleOverrides: { paper: { padding: 0 } } },
      MuiPopper: {
        styleOverrides: {
          root: {
            "&.MuiDataGrid-panel .MuiPaper-root, &.MuiDataGrid-menu .MuiPaper-root":
              { padding: "0.5rem", maxHeight: "350px" },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { "@media (min-width:600px)": { padding: "1.5rem" } },
        },
      },
      MuiTextField: {
        defaultProps: { variant: "outlined" },
        styleOverrides: { root: { width: "100%" } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: BUTTON_BORDER_RADIUS },
          notchedOutline: {
            borderRadius: BUTTON_BORDER_RADIUS,
          },
          input: {
            "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus":
              {
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: mainThemeColours.text[mode],
                WebkitBoxShadow: "0 0 0 1000px transparent inset",
                fontFamily: "inherit",
                fontSize: "inherit",
                caretColor: mainThemeColours.text[mode],
              },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { padding: 0, "&.MuiPaper-root": { padding: 0 } },
        },
      },
      MuiStack: {
        styleOverrides: { root: { justifyContent: "center", width: "100%" } },
      },
      MuiRadio: { defaultProps: { color: "secondary" } },
      MuiCheckbox: { defaultProps: { color: "secondary" } },
      MuiSwitch: { defaultProps: { color: "secondary" } },
      MuiLink: { defaultProps: { color: "info" } },
      MuiChip: {
        styleOverrides: { root: { paddingLeft: 3, paddingRight: 2 } },
        defaultProps: { variant: "outlined", size: "small" },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { paddingLeft: "1rem", paddingRight: "1rem" } },
      },
      MuiDialogContent: {
        styleOverrides: { root: { paddingLeft: "1rem", paddingRight: "1rem" } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { paddingLeft: "1rem", paddingRight: "1rem" } },
      },
      MuiCardActionArea: {
        styleOverrides: { root: { borderRadius: BORDER_RADIUS } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            // Selected: secondary background + secondary text/icon color
            "&.Mui-selected": {
              backgroundColor: alpha(mainThemeColours.secondary[mode], 0.15),
              "& .MuiListItemIcon-root": {
                color: mainThemeColours.secondary[mode],
              },
            },
            // Hover: use a lighter secondary background
            "&:hover": {
              backgroundColor: alpha(mainThemeColours.secondary[mode], 0.1),
            },
            // Hover while selected -> slightly stronger background
            "&.Mui-selected:hover": {
              backgroundColor: alpha(mainThemeColours.secondary[mode], 0.25),
            },
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            color: mainThemeColours.text[mode],
            "&.Mui-disabled": {
              color: mainThemeColours.altText[mode],
              opacity: 0.5,
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            color: mainThemeColours.text[mode],
            "&.Mui-disabled": {
              color: mainThemeColours.altText[mode],
              opacity: 0.5,
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            padding: "0.5rem",
            "& .MuiDataGrid-columnHeaderTitle": { textTransform: "capitalize" },
            "& .MuiDataGrid-toolbarContainer": {
              paddingTop: "0.5rem",
              paddingBottom: "0.5rem",
            },
            // this targets the quick filter text field in the toolbar
            "& .MuiDataGrid-toolbarContainer .MuiDataGrid-toolbarQuickFilter .MuiInputBase-root":
              {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: BUTTON_BORDER_RADIUS,
                padding: "0.25rem",
              },
            "& .MuiDataGrid-toolbarContainer .MuiDataGrid-toolbarQuickFilter .MuiInputBase-root:before, & .MuiDataGrid-toolbarContainer .MuiDataGrid-toolbarQuickFilter .MuiInputBase-root:after":
              { display: "none" },
          },
        },
      },
      MuiCollapse: { defaultProps: { timeout: "auto" } },
    },
  });

export default MBSTheme;
